/**
 * Customer Management (Back Office) — domain types.
 * Every entity carries id / createdAt / updatedAt per the feature-architecture rules.
 */

export type RiskStatus = 'none' | 'watch' | 'blocked';

export type ReasonCode =
  | 'chargeback_abuse'
  | 'refund_comp_abuse'
  | 'promo_abuse'
  | 'no_show'
  | 'behavioural'
  | 'other';

export type OriginChannel =
  | 'back_office_manual'
  | 'pos'
  | 'app'
  | 'online_qr'
  | 'reservation'
  | 'loyalty';

/** Audit actions on the risk flag. */
export type FlagAction = 'set' | 'changed' | 'cleared';

export const REASON_CODE_LABELS: Record<ReasonCode, string> = {
  chargeback_abuse: 'Chargeback abuse',
  refund_comp_abuse: 'Refund/Comp abuse',
  promo_abuse: 'Promo abuse',
  no_show: 'No-show',
  behavioural: 'Behavioural',
  other: 'Other',
};

export const ORIGIN_CHANNEL_LABELS: Record<OriginChannel, string> = {
  back_office_manual: 'Back Office (manual)',
  pos: 'POS',
  app: 'App',
  online_qr: 'Online / QR',
  reservation: 'Reservation',
  loyalty: 'Loyalty',
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  none: 'None',
  watch: 'Watch',
  blocked: 'Blocked',
};

/** Snapshot of the customer's current active risk flag. */
export interface RiskFlag {
  status: Exclude<RiskStatus, 'none'>; // watch | blocked
  reasonCode: ReasonCode;
  note?: string;
  setBy: string; // actor display name
  setAt: string; // ISO 8601
}

/** Append-only audit entry for every set / change / clear action. */
export interface FlagAuditEntry {
  id: string;
  action: FlagAction;
  fromStatus: RiskStatus;
  toStatus: RiskStatus;
  reasonCode?: ReasonCode;
  note?: string;
  actor: string;
  timestamp: string; // ISO 8601
}

export type OrderChannel = 'dine_in' | 'takeout' | 'delivery' | 'online' | 'kiosk' | 'app' | 'catering';
export type Daypart = 'breakfast' | 'lunch' | 'dinner' | 'late_night';

export const ORDER_CHANNEL_LABELS: Record<OrderChannel, string> = {
  dine_in: 'Dine-in',
  takeout: 'Takeout',
  delivery: 'Delivery',
  online: 'Online ordering',
  kiosk: 'Kiosk',
  app: 'Customer App',
  catering: 'Catering',
};

/** One completed order linked to the customer. Segment rules are checked against these. */
export interface CustomerOrder {
  id: string;
  at: string; // ISO 8601
  total: number; // currency amount
  outletId: string;
  channel: OrderChannel;
  daypart: Daypart;
  itemIds: string[];
  categoryIds: string[];
}

/** ─── V2: rich profile sub-types ─── */

/** Lifecycle state, distinct from the risk flag (CM-P1-2). */
export type LifecycleState = 'active' | 'inactive' | 'archived';

export const LIFECYCLE_LABELS: Record<LifecycleState, string> = {
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
};

/** An important date for the guest (birthday is separate; e.g. anniversary). */
export interface ImportantDate {
  id: string;
  label: string;
  date: string; // yyyy-mm-dd
}

/** Free-form key/value attribute beyond tags (CM-P0-6). */
export interface CustomAttribute {
  id: string;
  key: string;
  value: string;
}

/** Loyalty context surfaced on the profile (read-only mirror; CM-P0-5). */
export interface LoyaltyStatus {
  tier: string;
  points: number;
  memberSince: string; // ISO date
  /** The member has earned a reward and not used it yet. */
  hasUnredeemedReward?: boolean;
  /** Days until the member's oldest points expire; null when none are expiring. */
  pointsExpiringInDays?: number | null;
}

/** ─── Marketing consent (CM-P1-3) ─── */

/** The ways a customer can be sent marketing. */
export type Channel = 'sms' | 'email' | 'push';
export const CHANNELS: Channel[] = ['sms', 'email', 'push'];

export const CHANNEL_LABELS: Record<Channel, string> = { sms: 'SMS', email: 'Email', push: 'Push' };

export type ConsentStatus = 'granted' | 'revoked';

/** Where a consent change was captured. `portal_staff` can only ever record an opt-out. */
export type ConsentSource =
  | 'pos' | 'cfd' | 'online_ordering' | 'customer_app' | 'kiosk'
  | 'sms_keyword' | 'email_link' | 'portal_staff' | 'support';

export const CONSENT_SOURCE_LABELS: Record<ConsentSource, string> = {
  pos: 'POS',
  cfd: 'Customer display',
  online_ordering: 'Online ordering',
  customer_app: 'Customer App',
  kiosk: 'Kiosk',
  sms_keyword: 'SMS reply',
  email_link: 'Unsubscribe link',
  portal_staff: 'Staff, in the portal',
  support: 'Support',
};

/**
 * One change to a customer's marketing consent on one channel.
 * Records are only ever added; the latest record per channel is the current status.
 */
export interface ConsentRecord {
  id: string;
  channel: Channel;
  status: ConsentStatus;
  source: ConsentSource;
  /** Version of the consent wording the customer was shown. */
  consentTextVersion: string;
  capturedAt: string; // ISO 8601
  /** Staff member who recorded it; null when the customer did it themselves. */
  capturedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Saved card on file for fast checkout (CM-P2-1). Mock only — no PAN stored. */
export interface SavedCard {
  id: string;
  brand: string; // Visa, Mastercard, Amex, Discover…
  last4: string;
  expMonth: number; // 1–12
  expYear: number; // 4-digit
  isDefault: boolean;
  addedAt: string; // ISO 8601
}

export interface Customer {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  birthday?: string; // ISO date (yyyy-mm-dd)
  notes?: string;
  tags: string[];
  riskStatus: RiskStatus;
  activeFlag?: RiskFlag;
  originChannel: OriginChannel;
  firstVisitAt?: string; // ISO 8601
  lastVisitAt?: string; // ISO 8601
  lifetimeSpend: number;
  visitCount: number;
  /** Linked completed orders, oldest first. */
  orders: CustomerOrder[];
  /** The outlet the customer orders from most. */
  preferredOutletId?: string;
  /** The customer has the Customer App installed and has signed in to it. */
  appInstalled: boolean;
  /** The app can deliver push notifications to this customer's device. */
  pushEnabled: boolean;
  auditLog: FlagAuditEntry[];
  // V2 fields
  lifecycleState: LifecycleState;
  preferences?: string;
  dietaryNotes?: string;
  allergyNotes?: string;
  importantDates: ImportantDate[];
  loyaltyStatus?: LoyaltyStatus | null;
  favouriteItems: string[];
  customAttributes: CustomAttribute[];
  groupIds: string[];
  /** Marketing consent history, oldest first. Add to it; never edit or remove a record. */
  marketingConsent: ConsentRecord[];
  savedCards: SavedCard[];
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/** Fields collected by the create / edit form. */
export interface CustomerFormInput {
  name: string;
  phone: string;
  email: string;
  birthday: string;
  notes: string;
  tags: string[];
  preferences: string;
  dietaryNotes: string;
  allergyNotes: string;
  importantDates: ImportantDate[];
  customAttributes: CustomAttribute[];
}

/** ─── V2: manual group (CM-P0-6) ─── */
export interface CustomerGroup {
  id: string;
  name: string;
  description?: string;
  memberIds: string[];
  createdAt: string;
  updatedAt: string;
}

/** ─── V2: smart segment (CM-P0-7) ─── */

export type RuleOperator = 'lt' | 'gt' | 'between' | 'eq' | 'gte' | 'lte' | 'in' | 'not_in';

export type RuleField =
  | 'last_order_days_ago' | 'first_order_days_ago' | 'order_count' | 'total_spend_cents'
  | 'avg_order_value_cents' | 'ordered_item_id' | 'ordered_category_id' | 'order_channel'
  | 'outlet_id' | 'daypart' | 'loyalty_member' | 'loyalty_tier' | 'points_balance'
  | 'has_unredeemed_reward' | 'points_expiring_within_days' | 'birthday_month' | 'has_app'
  | 'tag' | 'group_id' | 'risk_status' | 'lifecycle_state'
  | 'received_campaign_id' | 'redeemed_campaign_id'
  // Used by prebuilt segments only; the rule builder does not offer them.
  | 'spend_top_percent' | 'birthday_this_month' | 'reachable';

export type RuleValue = number | boolean | string | [number, number] | string[];

/** One condition. Money values are whole cents. */
export interface RuleCondition {
  field: RuleField | string;
  operator: RuleOperator | string;
  value: RuleValue;
  /** Only count orders from the last N days. */
  periodDays?: number;
}

/** Conditions joined by "and" or "or". A group may hold one more level of groups. */
export interface RuleGroup {
  op: 'and' | 'or';
  children: RuleNode[];
}

export type RuleNode = RuleGroup | RuleCondition;

export type SegmentType = 'prebuilt' | 'custom';

export interface Segment {
  id: string;
  name: string;
  description: string;
  type: SegmentType;
  /** Stable key of a prebuilt segment, e.g. SEG-03; null for custom segments. */
  prebuiltKey: string | null;
  rules: RuleGroup;
  createdAt: string;
  updatedAt: string;
}

/** A choice offered by a list-valued rule field. */
export interface RuleOption {
  id: string;
  name: string;
}

/** Toast notification item. */
export interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}
